import Axios, { AxiosError } from "axios";
import { round } from "lodash-es";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { APIError, PointSource } from "src/types";
import { pointSources as decimalScale } from "src/config/decimal-scales";
import { pointSourceKeys } from "src/query-keys";

export type PointSourceMutationData = Omit<PointSource, "id">;
export type PointSourceCreateMutationData = PointSourceMutationData & {
  point_frontendSrid: string;
};

/**
 * Deserialize the json format into a Javascript object
 */
function deserialize(data: PointSource): PointSource {
  return {
    ...data,
    nonox: round(data.nonox, decimalScale.nonox),
    height_m: round(data.height_m, decimalScale.height_m),
    flowrate_Nm3_s: round(data.flowrate_Nm3_s, decimalScale.flowrate_Nm3_s),
    diameter_m: round(data.diameter_m, decimalScale.diameter_m),
    temperature_K: round(data.temperature_K, decimalScale.temperature_K),
    emissions_kg_h: data.emissions_kg_h.map((emission) => {
      return {
        ...emission,
        emission_kg_h: round(emission.emission_kg_h, decimalScale.emissions_kg_h),
      };
    }),
  };
}
export function usePointSource(
  scenarioId: number | string,
  emissionId: number | string,
  pointSourceId: number | string
) {
  async function queryFn(
    scenarioId: number | string,
    emissionId: number | string,
    pointSourceId: number | string
  ): Promise<PointSource> {
    const response = await Axios.get(
      `/planning-api/scenarios/${scenarioId}/pointsourceemissions/${emissionId}/pointsources/${pointSourceId}`
    );
    return deserialize(response.data);
  }

  return useQuery<PointSource, APIError>(pointSourceKeys.detail(scenarioId, emissionId, pointSourceId), () =>
    queryFn(scenarioId, emissionId, pointSourceId)
  );
}

export function usePointSourceCreateMutation(scenarioId: number | string, emissionId: number | string) {
  const queryClient = useQueryClient();
  async function mutationFn(
    scenarioId: number | string,
    emissionId: number | string,
    data: PointSourceCreateMutationData
  ) {
    const response = await Axios.post(
      `/planning-api/scenarios/${scenarioId}/pointsourceemissions/${emissionId}/pointsources`,
      data
    );

    queryClient.invalidateQueries("wfs-get-feature");
    return response.data;
  }

  return useMutation<PointSource, AxiosError<APIError[]>, PointSourceCreateMutationData>(
    (values: PointSourceCreateMutationData) => mutationFn(scenarioId, emissionId, values)
  );
}

export function usePointSourceUpdateMutation(
  scenarioId: number | string,
  emissionId: number | string,
  pointSourceId: number | string
) {
  const queryClient = useQueryClient();

  async function mutationFn(
    scenarioId: number | string,
    emissionId: number | string,
    pointSourceId: number | string,
    data: PointSourceMutationData
  ): Promise<void> {
    return Axios.put(
      `/planning-api/scenarios/${scenarioId}/pointsourceemissions/${emissionId}/pointsources/${pointSourceId}`,
      data
    );
  }
  return useMutation<void, AxiosError<APIError[]>, PointSource, unknown>(
    (values: PointSourceMutationData) => mutationFn(scenarioId, emissionId, pointSourceId, values),
    {
      onMutate: async (updatedPointSource: PointSource) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(pointSourceKeys.detail(scenarioId, emissionId, pointSourceId));

        // Optimistically update to the new value
        queryClient.setQueryData(
          pointSourceKeys.detail(scenarioId, emissionId, pointSourceId),
          () => updatedPointSource
        );
      },
      onSettled: async () => {
        return await queryClient.invalidateQueries(pointSourceKeys.detail(scenarioId, emissionId, pointSourceId));
      },
    }
  );
}

export function usePointSourceDeleteMutation(
  scenarioId: number | string,
  emissionId: number | string,
  pointSourceId: number | string
) {
  const queryClient = useQueryClient();
  async function mutationFn(scenarioId: number | string, emissionId: number | string, pointSourceId: number | string) {
    const response = await Axios.delete(
      `/planning-api/scenarios/${scenarioId}/pointsourceemissions/${emissionId}/pointsources/${pointSourceId}`
    );
    queryClient.invalidateQueries("wfs-get-feature");
    return response.data;
  }

  return useMutation(() => mutationFn(scenarioId, emissionId, pointSourceId));
}
